import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { Vector2 } from 'three';

const pointerPosition = new Vector2();
function computePointerPosition(x: number, y: number, rect: DOMRect): Vector2 {
    pointerPosition.set(x - rect.left, y - rect.top);
    return pointerPosition;
}

interface SelectionHelperProps {
    cssClassName: string;
    isEnabled?: (event: PointerEvent) => boolean;
    onSelectStart?: (event: PointerEvent) => void;
    onSelectMove?: (event: PointerEvent) => void;
    onSelectOver?: (event: PointerEvent) => void;
}

export const useSelectionHelper = ({
    cssClassName,
    isEnabled = () => true,
    onSelectStart,
    onSelectMove,
    onSelectOver
}: SelectionHelperProps) => {
    const gl = useThree((state) => state.gl);

    const elementRef = useRef<HTMLDivElement | null>(null);
    const startPoint = useRef<Vector2>(new Vector2());
    const pointTopLeft = useRef<Vector2>(new Vector2());
    const pointBottomRight = useRef<Vector2>(new Vector2());
    const isDownRef = useRef<boolean>(false);
    const cachedRectRef = useRef<DOMRect | null>(null);

    const minDragDistance = 3;
    const hasDraggedRef = useRef<boolean>(false);

    useEffect(() => {
        elementRef.current = document.createElement('div');
        elementRef.current.classList.add(cssClassName);
        elementRef.current.style.setProperty('pointerEvents', 'none');

        const handleOnPointerDown = (event: PointerEvent) => {
            if (!isEnabled(event) || !elementRef.current) {
                return;
            }

            isDownRef.current = true;
            hasDraggedRef.current = false;
            cachedRectRef.current = gl.domElement.getBoundingClientRect();

            elementRef.current.style.setProperty('display', 'none');
            gl.domElement.parentElement?.appendChild(elementRef.current);

            const pointerPosition = computePointerPosition(event.clientX, event.clientY, cachedRectRef.current);
            startPoint.current.copy(pointerPosition);

            elementRef.current.style.setProperty('left', `${pointerPosition.x}px`);
            elementRef.current.style.setProperty('top', `${pointerPosition.y}px`);
            elementRef.current.style.setProperty('width', '0px');
            elementRef.current.style.setProperty('height', '0px');

            onSelectStart?.(event);
        };

        const handleOnPointerMove = (event: PointerEvent) => {
            if (!isEnabled(event) || !isDownRef.current || !elementRef.current || !cachedRectRef.current) {
                return;
            }

            const endPoint = computePointerPosition(event.clientX, event.clientY, cachedRectRef.current);

            pointBottomRight.current.set(
                Math.max(startPoint.current.x, endPoint.x),
                Math.max(startPoint.current.y, endPoint.y)
            );

            pointTopLeft.current.set(
                Math.min(startPoint.current.x, endPoint.x),
                Math.min(startPoint.current.y, endPoint.y)
            );

            elementRef.current.style.setProperty('left', `${pointTopLeft.current.x}px`);
            elementRef.current.style.setProperty('top', `${pointTopLeft.current.y}px`);
            elementRef.current.style.setProperty('width', `${pointBottomRight.current.x - pointTopLeft.current.x}px`);
            elementRef.current.style.setProperty('height', `${pointBottomRight.current.y - pointTopLeft.current.y}px`);

            // Only make the visual box visible once the pointer has moved a
            // meaningful distance — this prevents a visual flash on simple clicks.
            const width = pointBottomRight.current.x - pointTopLeft.current.x;
            const height = pointBottomRight.current.y - pointTopLeft.current.y;
            if (!hasDraggedRef.current && Math.max(width, height) >= minDragDistance) {
                hasDraggedRef.current = true;
            }
            if (hasDraggedRef.current) {
                elementRef.current.style.setProperty('display', 'block');
            }

            onSelectMove?.(event);
        };

        const handleOnPointerUp = (event: PointerEvent) => {
            if (!isEnabled(event) || !elementRef.current) {
                return;
            }

            isDownRef.current = false;
            cachedRectRef.current = null;
            elementRef.current.style.setProperty('display', 'none');
            onSelectOver?.(event);
        };

        window.addEventListener('pointerdown', handleOnPointerDown);
        window.addEventListener('pointermove', handleOnPointerMove);
        window.addEventListener('pointerup', handleOnPointerUp);

        return () => {
            window.removeEventListener('pointerdown', handleOnPointerDown);
            window.removeEventListener('pointermove', handleOnPointerMove);
            window.removeEventListener('pointerup', handleOnPointerUp);
            elementRef.current?.remove();
        };
    }, [cssClassName, isEnabled, gl.domElement, onSelectMove, onSelectOver, onSelectStart]);
};
